import { Box, Button, IconButton, Tooltip, useTheme } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { OverrideSchedule, DateOnly, DailySchedule, isDateRange, INITIAL_STATE_ARMED } from "../../../api/AutoArmConfig"
import { ScheduleSetter } from "./ScheduleSetter"
import DeleteIcon from "@mui/icons-material/Delete"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"
import AddIcon from "@mui/icons-material/Add"
import { useCallback, useEffect, useState } from "react"

type ErrorStates = {
    dateStartError: string
    dateEndError: string
    scheduleError: boolean
}

type Props = {
    value: OverrideSchedule[]
    readOnly: boolean
    onChange: (overrides: OverrideSchedule[]) => void
    onError: (hasError: boolean) => void
}

export const AutoArmCustom = ({ value, readOnly, onChange, onError }: Props) => {
    const { t } = useTranslation()
    const theme = useTheme()

    const [errorStates, setErrorStates] = useState<ErrorStates[]>(
        value.map(() => ({ dateStartError: "", dateEndError: "", scheduleError: false }))
    )

    const handleAdd = () => {
        const today = DateTime.now()
        const defaultDate: DateOnly = {
            year: today.year,
            month: today.month,
            day: today.day,
        }

        const newOverride: OverrideSchedule = {
            condition: { dateRange: { start: defaultDate, end: defaultDate } },
            schedule: { initial: INITIAL_STATE_ARMED, transitions: [] },
        }

        onChange([...value, newOverride])
    }

    const handleStartChange = (index: number, date: DateTime | null) => {
        if (!date || !date.isValid) {
            return
        }

        const updated = [...value]
        const overrideToUpdate = updated[index]

        if (overrideToUpdate && isDateRange(overrideToUpdate.condition)) {
            overrideToUpdate.condition.dateRange.start = {
                year: date.year,
                month: date.month,
                day: date.day,
            }
            onChange(updated)
        }
    }

    const handleEndChange = (index: number, date: DateTime | null) => {
        if (!date || !date.isValid) {
            return
        }

        const updated = [...value]
        const overrideToUpdate = updated[index]

        if (overrideToUpdate && isDateRange(overrideToUpdate.condition)) {
            overrideToUpdate.condition.dateRange.end = {
                year: date.year,
                month: date.month,
                day: date.day,
            }
            onChange(updated)
        }
    }

    const handleScheduleChange = useCallback(
        (index: number, newSchedule: DailySchedule) => {
            const updated = [...value]
            updated[index] = {
                ...updated[index],
                schedule: newSchedule,
            }
            onChange(updated)
        },
        [value, onChange]
    )

    const handleRemove = (index: number) => {
        const updated = value.filter((_, i) => i !== index)
        onChange(updated)
    }

    const isDateAlreadyUsed = useCallback(
        (date: DateTime, indexToIgnore: number | null): boolean => {
            return value.some((override, i) => {
                if ((indexToIgnore !== null && i === indexToIgnore) || !isDateRange(override.condition)) {
                    return false
                }
                const { start, end } = override.condition.dateRange
                return date >= DateTime.fromObject(start) && date <= DateTime.fromObject(end)
            })
        },
        [value]
    )

    const updateScheduleErrorState = useCallback((index: number, errorValue: boolean) => {
        setErrorStates((prev) => {
            const current = prev[index]

            if (current?.scheduleError === errorValue) {
                return prev
            }

            const newErrorStates = [...prev]

            newErrorStates[index] = {
                ...current,
                dateStartError: current?.dateStartError ?? "",
                dateEndError: current?.dateEndError ?? "",
                scheduleError: errorValue,
            }

            return newErrorStates
        })
    }, [])

    useEffect(() => {
        setErrorStates((prev) =>
            value.map((override, index) => {
                let dateStartError = ""
                let dateEndError = ""
                const scheduleError = prev[index]?.scheduleError ?? false

                if (!isDateRange(override.condition)) {
                    return prev[index]
                }

                const start = DateTime.fromObject(override.condition.dateRange.start)
                const end = DateTime.fromObject(override.condition.dateRange.end)

                if (!start.isValid) {
                    dateStartError = t("dialog.auto_arm.invalid_date")
                }

                if (!end.isValid) {
                    dateEndError = t("dialog.auto_arm.invalid_date")
                }

                if (end < start) {
                    dateEndError = t("dialog.auto_arm.end_date_cannot_be_before_start_date")
                }

                if (isDateAlreadyUsed(start, index)) {
                    dateStartError = t("dialog.auto_arm.date_range_cannot_overlap")
                }
                if (isDateAlreadyUsed(end, index)) {
                    dateEndError = t("dialog.auto_arm.date_range_cannot_overlap")
                }

                return { dateStartError, dateEndError, scheduleError }
            })
        )
    }, [value, isDateAlreadyUsed, t])

    useEffect(() => {
        onError(errorStates.some((state) => !!state.dateStartError || !!state.dateEndError || state.scheduleError))
    }, [errorStates, onError])

    const renderDateRangeOverride = (override: OverrideSchedule, index: number) => {
        if (!isDateRange(override.condition)) {
            return null
        }

        const start = DateTime.fromObject(override.condition.dateRange.start)
        const end = DateTime.fromObject(override.condition.dateRange.end)

        return (
            <Box key={index} mt={2} p={2} border={`1px solid ${theme.palette.grey[400]}`} borderRadius={2}>
                <Box display="flex" gap={2} alignItems="center" justifyContent="space-between">
                    <Tooltip
                        title={errorStates[index]?.dateStartError || ""}
                        disableHoverListener={!errorStates[index]?.dateStartError}
                    >
                        <div>
                            <DatePicker
                                disabled={readOnly}
                                label={t("start")}
                                value={start}
                                onChange={(date) => handleStartChange(index, date)}
                                slotProps={{
                                    textField: {
                                        error: !!errorStates[index]?.dateStartError,
                                    },
                                }}
                            />
                        </div>
                    </Tooltip>

                    <Tooltip
                        title={errorStates[index]?.dateEndError || ""}
                        disableHoverListener={!errorStates[index]?.dateEndError}
                    >
                        <div>
                            <DatePicker
                                disabled={readOnly}
                                label={t("end")}
                                value={end}
                                onChange={(date) => handleEndChange(index, date)}
                                slotProps={{
                                    textField: {
                                        error: !!errorStates[index]?.dateEndError,
                                    },
                                }}
                            />
                        </div>
                    </Tooltip>

                    <IconButton disabled={readOnly} onClick={() => handleRemove(index)}>
                        <DeleteIcon />
                    </IconButton>
                </Box>

                <Box mt={2}>
                    <ScheduleSetter
                        value={override.schedule}
                        readOnly={readOnly}
                        onChange={(schedule) => handleScheduleChange(index, schedule)}
                        onError={(hasError) => updateScheduleErrorState(index, hasError)}
                    />
                </Box>
            </Box>
        )
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
            {value.map((o, index) => renderDateRangeOverride(o, index))}
            <Button disabled={readOnly} startIcon={<AddIcon />} variant="outlined" onClick={handleAdd}>
                {t("action.add_date_range")}
            </Button>
        </Box>
    )
}
